import type { FC, ReactNode } from "react";
import { useMemo } from "react";

import { replaceStringPlaceholder } from "@ui/utils/replaceStringPlaceholder/replaceStringPlaceholder";
import classNames from "classnames";
import dompurify from "isomorphic-dompurify";

import { Button } from "@jti/core-components/components/Button";
import { dispatchTrackingEvent, TrackingEvents } from "@jti/tracker";

import { ReferralPersonalCodeBlocks } from "../../ReferralPersonalCodeBlocks";
import type { ReferralPersonalCodeIconPropsType } from "../../ReferralPersonalCodeIcon";
import { ReferralPersonalCodeSmallText } from "../../ReferralPersonalCodeSmallText";
import { ReferralPersonalCodeWithBackground } from "../ReferralPersonalCodeWithBackground/ReferralPersonalCodeWithBackground";

import styles from "./ReferralPersonalCodeVariantIcon.module.scss";

const { sanitize } = dompurify;

export type ReferralPersonalCodeVariantIconPropsType = {
  topHeader?: string;
  code: string;
  header?: string;
  codeTitle?: string;
  shareTitle?: string;
  description?: string;
  variant?: "default" | "dashboard" | "withBackgroundImage";
  referralPageVariant?: boolean;
  items: ReferralPersonalCodeIconPropsType[];
  infoTitle?: string;
  smallText?: string;
  backgroundImage?: string;
  backgroundImageTablet?: string;
  backgroundImageMobile?: string;
  showCta?: boolean;
  ctaRow?: ReactNode;
  className?: string;
  withBottomCta?: boolean;
  bottomCtaLabel?: string;
  bottomCtaLink?: string;
  isHidden?: boolean;
  isLegacyStyled?: boolean;
};

export const ReferralPersonalCodeVariantIcon: FC<ReferralPersonalCodeVariantIconPropsType> = ({
  topHeader,
  code,
  header,
  codeTitle,
  shareTitle,
  description,
  variant = "default",
  items = [],
  infoTitle,
  backgroundImage,
  backgroundImageTablet,
  backgroundImageMobile,
  smallText,
  showCta = false,
  ctaRow,
  className,
  withBottomCta = false,
  bottomCtaLabel = "",
  bottomCtaLink = "",
  isHidden = false,
  isLegacyStyled = false,
}: ReferralPersonalCodeVariantIconPropsType) => {
  const isWithBackgroundImageVariant = variant === "withBackgroundImage";

  const replaceUrlCode = (url: string, code: string) => {
    return replaceStringPlaceholder(
      url,
      [
        {
          placeholder: "referralCode",
          value: code,
        },
      ],
      false
    );
  };

  const itemsUrlCodeConverted = useMemo(() => {
    if (items.length === 0 || !code) return [];
    return items.map((element: ReferralPersonalCodeIconPropsType) => ({
      ...element,
      url: replaceUrlCode(element.url, code),
    }));
  }, [items, code]);

  if (isHidden) {
    return null;
  }

  const handleBottomCtaClick = () => {
    dispatchTrackingEvent(TrackingEvents.TrackingCtaClick, {
      cta_name: bottomCtaLabel,
      cta_location: "referral-personal-code",
    });
  };

  if (isWithBackgroundImageVariant) {
    return (
      <div className={classNames({ [styles.legacyStyle]: isLegacyStyled })}>
        <ReferralPersonalCodeWithBackground
          items={itemsUrlCodeConverted}
          backgroundImage={backgroundImage}
          backgroundImageTablet={backgroundImageTablet}
          backgroundImageMobile={backgroundImageMobile}
          title={codeTitle}
          subtitle={header}
          description={description}
          code={code}
          infoTitle={infoTitle}
          withBottomCta={withBottomCta}
          bottomCtaLabel={bottomCtaLabel}
          bottomCtaLink={bottomCtaLink}
          handleBottomCtaClick={handleBottomCtaClick}
          ctaRow={ctaRow}
        />
      </div>
    );
  }

  return (
    <div className={classNames({ [styles.legacyStyle]: isLegacyStyled })}>
      {topHeader && (
        <div className={styles.topHeader} data-testid="referral-personal-code__topHeader">
          {topHeader}
        </div>
      )}
      <div className={classNames(styles.personalCodeWrapper, className)}>
        <div
          className={classNames(styles.personalCode, {
            [styles.dashboard]: variant === "dashboard",
          })}
          data-testid="referral-personal-code"
        >
          <div className={styles.title} data-testid={"your-personal-code-header"}>
            {codeTitle}
          </div>
          {description && (
            <div
              className={styles.description}
              dangerouslySetInnerHTML={{
                __html: sanitize(description),
              }}
            />
          )}
          {showCta && ctaRow ? (
            ctaRow
          ) : (
            <ReferralPersonalCodeBlocks
              code={code}
              items={itemsUrlCodeConverted}
              infoTitle={infoTitle}
              shareTitle={shareTitle ?? ""}
            />
          )}
          <ReferralPersonalCodeSmallText
            smallText={smallText}
            isWithBackgroundImageVariant={isWithBackgroundImageVariant}
          />
          {withBottomCta && (
            <Button
              variant="text"
              type="cta"
              size="lg"
              href={bottomCtaLink}
              onClick={handleBottomCtaClick}
              data-testid="bottomCta"
            >
              {bottomCtaLabel}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};
